import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid
} from '@material-ui/core';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Componente de alerta personalizado
const CustomAlert = ({ severity, children, style = {} }) => {
  const getColor = () => {
    switch (severity) {
      case 'success': return '#4caf50';
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      case 'info': return '#2196f3';
      default: return '#2196f3';
    }
  };

  const getBackgroundColor = () => {
    switch (severity) {
      case 'success': return '#e8f5e8';
      case 'error': return '#ffebee';
      case 'warning': return '#fff3e0';
      case 'info': return '#e3f2fd';
      default: return '#e3f2fd';
    }
  };

  return (
    <Box
      style={{
        padding: '12px 16px',
        borderRadius: '4px',
        border: `1px solid ${getColor()}`,
        backgroundColor: getBackgroundColor(),
        color: getColor(),
        marginBottom: '16px',
        ...style
      }}
    >
      {children}
    </Box>
  );
};

const DuplicateTicketsManager = () => {
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, data: null });

  // Cargar tickets duplicados
  const loadDuplicateTickets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/duplicate-tickets/analyze');
      setDuplicateGroups(response.data);
    } catch (error) {
      console.error('Error loading duplicate tickets:', error);
      toast.error('Error al cargar tickets duplicados');
    } finally {
      setLoading(false);
    }
  };

  // Ejecutar corrección
  const executeCorrection = async (groupId, action) => {
    setProcessing(true);
    try {
      await api.post('/duplicate-tickets/fix', {
        groupId,
        action,
        targetTicketId: action.targetTicketId,
        sourceTicketIds: action.sourceTicketIds
      });
      
      toast.success('Corrección aplicada exitosamente');
      await loadDuplicateTickets(); // Recargar datos
    } catch (error) {
      console.error('Error executing correction:', error);
      toast.error('Error al aplicar corrección');
    } finally {
      setProcessing(false);
      setConfirmDialog({ open: false, data: null });
    }
  };

  // Abrir diálogo de confirmación
  const openConfirmDialog = (group, action) => {
    setSelectedAction(action);
    setConfirmDialog({
      open: true,
      data: {
        group,
        action,
        message: `¿Estás seguro de que quieres ${action.type === 'migrate' ? 'migrar' : 'eliminar'} los tickets seleccionados?`
      }
    });
  };

  // Confirmar acción
  const confirmAction = () => {
    if (selectedAction) {
      executeCorrection(selectedAction.groupId, selectedAction);
    }
  };

  // Determinar si un número es LID
  const isLid = (number) => {
    if (!number) return false;
    return number.length > 15 || number.startsWith('217823611719792');
  };

  // Determinar si un número es real
  const isRealNumber = (number) => {
    if (!number) return false;
    return /^\d{8,15}$/.test(number) && !isLid(number);
  };

  // Estado para selección manual del usuario
  const [selectedTickets, setSelectedTickets] = useState({});

  // Manejar selección de ticket principal
  const handleTicketSelection = (groupId, ticketId) => {
    setSelectedTickets(prev => ({
      ...prev,
      [groupId]: ticketId
    }));
  };

  // Obtener recomendación basada en selección del usuario
  const getRecommendation = (group, groupIndex) => {
    const selectedTicketId = selectedTickets[groupIndex];
    
    if (!selectedTicketId) {
      return {
        type: 'select',
        reason: 'Selecciona el ticket que quieres mantener',
        description: 'Haz clic en el botón "Mantener" del ticket que contiene el número correcto'
      };
    }

    const selectedTicket = group.tickets.find(t => t.id === selectedTicketId);
    const otherTickets = group.tickets.filter(t => t.id !== selectedTicketId);
    
    if (otherTickets.length > 0) {
      return {
        type: 'migrate',
        targetTicketId: selectedTicketId,
        sourceTicketIds: otherTickets.map(t => t.id),
        reason: `Migrar mensajes al ticket seleccionado por el usuario`,
        description: `Mantener ticket #${selectedTicketId} (${selectedTicket.contactName} - ${selectedTicket.contactNumber}) y migrar mensajes de los otros tickets`
      };
    }
    
    return null;
  };

  useEffect(() => {
    loadDuplicateTickets();
  }, []);

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestor de Tickets Duplicados
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={loadDuplicateTickets}
          disabled={loading}
        >
          Actualizar
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : duplicateGroups.length === 0 ? (
        <CustomAlert severity="success">
          <span role="img" aria-label="éxito">✅</span> No se encontraron tickets duplicados
        </CustomAlert>
      ) : (
        <Grid container spacing={3}>
          {duplicateGroups.map((group, groupIndex) => {
            const recommendation = getRecommendation(group);
            
            return (
              <Grid item xs={12} key={groupIndex}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">
                        WhatsApp ID: {group.whatsappId} - Company: {group.companyId}
                      </Typography>
                      <Chip
                        label={`${group.tickets.length} tickets`}
                        color="secondary"
                        size="small"
                      />
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Ticket ID</TableCell>
                            <TableCell>Contacto</TableCell>
                            <TableCell>Número</TableCell>
                            <TableCell>Mensajes</TableCell>
                            <TableCell>No Leídos</TableCell>
                            <TableCell>Última Actividad</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.tickets.map((ticket) => (
                            <TableRow key={ticket.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  #{ticket.id}
                                </Typography>
                              </TableCell>
                              <TableCell>{ticket.contactName}</TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color={isRealNumber(ticket.contactNumber) ? 'primary' : 'error'}
                                >
                                  {ticket.contactNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>{ticket.messageCount}</TableCell>
                              <TableCell>
                                <Chip
                                  label={ticket.unreadMessages}
                                  color={ticket.unreadMessages > 0 ? 'secondary' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(ticket.updatedAt).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {isRealNumber(ticket.contactNumber) ? (
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Número Real"
                                    color="primary"
                                    size="small"
                                  />
                                ) : isLid(ticket.contactNumber) ? (
                                  <Chip
                                    icon={<WarningIcon />}
                                    label="LID Interno"
                                    color="secondary"
                                    size="small"
                                  />
                                ) : (
                                  <Chip
                                    icon={<InfoIcon />}
                                    label="Desconocido"
                                    color="default"
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {selectedTickets[groupIndex] === ticket.id ? (
                                  <Chip
                                    label="Seleccionado"
                                    color="primary"
                                    size="small"
                                  />
                                ) : selectedTickets[groupIndex] ? (
                                  <Chip
                                    label="Eliminar"
                                    color="secondary"
                                    size="small"
                                  />
                                ) : (
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleTicketSelection(groupIndex, ticket.id)}
                                  >
                                    Mantener
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {recommendation && (
                      <Box mt={2}>
                        {recommendation.type === 'select' ? (
                          <CustomAlert severity="warning" style={{ marginBottom: 16 }}>
                            <Typography variant="body2">
                              <strong><span role="img" aria-label="advertencia">⚠️</span> Acción requerida:</strong> {recommendation.reason}
                            </Typography>
                            <Typography variant="body2">
                              {recommendation.description}
                            </Typography>
                            <Typography variant="body2" style={{ marginTop: 8, fontWeight: 'bold' }}>
                              <span role="img" aria-label="idea">💡</span> Tip: Revisa los números y nombres de contacto para identificar cuál es el correcto
                            </Typography>
                          </CustomAlert>
                        ) : (
                          <CustomAlert severity="info" style={{ marginBottom: 16 }}>
                            <Typography variant="body2">
                              <strong><span role="img" aria-label="éxito">✅</span> Corrección preparada:</strong> {recommendation.reason}
                            </Typography>
                            <Typography variant="body2">
                              {recommendation.description}
                            </Typography>
                          </CustomAlert>
                        )}
                        
                        {recommendation.type !== 'select' && (
                          <Box display="flex" gap={2}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => openConfirmDialog(group, {
                                ...recommendation,
                                groupId: groupIndex
                              })}
                              disabled={processing}
                            >
                              Aplicar Corrección
                            </Button>
                            
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                // Mostrar detalles de la acción
                                toast.info(`Acción: ${recommendation.reason}`);
                              }}
                            >
                              Ver Detalles
                            </Button>

                            <Button
                              variant="outlined"
                              color="default"
                              onClick={() => {
                                setSelectedTickets(prev => ({
                                  ...prev,
                                  [groupIndex]: null
                                }));
                                toast.info('Selección limpiada. Puedes elegir otro ticket.');
                              }}
                            >
                              Cambiar Selección
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Diálogo de Confirmación */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, data: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirmar Corrección de Tickets Duplicados
        </DialogTitle>
        <DialogContent>
          {confirmDialog.data && (
            <Box>
              <Typography variant="body1" paragraph>
                {confirmDialog.data.message}
              </Typography>
              
              <CustomAlert severity="warning" style={{ marginBottom: 16 }}>
                <Typography variant="body2">
                  <strong>Acción a realizar:</strong> {confirmDialog.data.action.reason}
                </Typography>
              </CustomAlert>

              <Typography variant="body2" color="textSecondary">
                Esta acción no se puede deshacer. Se recomienda hacer un backup de la base de datos antes de continuar.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, data: null })}
            disabled={processing}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmAction}
            color="primary"
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {processing ? 'Procesando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DuplicateTicketsManager;
